/**
 * A QR scan that happens ON THE PHONE. The camera picture never leaves the
 * device and no outside service is involved: it uses the browser's own
 * BarcodeDetector. Where the browser has none, the button simply does not
 * appear and the code is typed instead — typing is always available.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const canScan = () => typeof window !== 'undefined' && 'BarcodeDetector' in window;

export const QrScan = ({ onFound, onClose }) => {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const [trouble, setTrouble] = useState('');

    useEffect(() => {
        let stream;
        let stop = false;
        const run = async () => {
            try {
                stream = await navigator.mediaDevices.getUserMedia(
                    { video: { facingMode: 'environment' } });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }
                // eslint-disable-next-line no-undef
                const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
                const tick = async () => {
                    if (stop) return;
                    try {
                        const codes = await detector.detect(videoRef.current);
                        if (codes && codes.length) {
                            onFound(String(codes[0].rawValue || '').trim());
                            return;
                        }
                    } catch { /* a frame that could not be read — try the next */ }
                    setTimeout(tick, 400);
                };
                tick();
            } catch {
                setTrouble(t('loyalty.desk.noCamera', 'No camera here — type the code instead.'));
            }
        };
        run();
        return () => {
            stop = true;
            if (stream) stream.getTracks().forEach((track) => track.stop());
        };
    }, [onFound, t]);

    return (
        <div className="space-y-2" data-testid="loyalty-desk-scanner">
            <video ref={videoRef} playsInline muted
                   className="w-full rounded-xl border border-white/20"
                   data-testid="loyalty-desk-scanner-video" />
            {trouble && (
                <p className="text-center text-sm" data-testid="loyalty-desk-scanner-trouble">
                    {trouble}
                </p>
            )}
            <button type="button" onClick={onClose} className="w-full text-center text-sm underline"
                    data-testid="loyalty-desk-scanner-close">
                {t('common.close', 'Close')}
            </button>
        </div>
    );
};

export default QrScan;
